'use client';

import {
    FacebookIcon,
    FacebookShareButton,
    TelegramIcon,
    TelegramShareButton,
    TwitterShareButton,
    WhatsappIcon,
    WhatsappShareButton,
    XIcon,
} from 'react-share';
import { Copy, Share2Icon } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Recipe } from '@/types/recipe';

interface ShareButtonProps {
    recipe?: Recipe;
}

export const ShareButton = ({ recipe }: ShareButtonProps) => {
    const pathname = usePathname();
    const t = useTranslations();
    const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}${pathname}` : '';

    // Prepare share data
    const title = recipe?.title || '';
    const hashtags = ['MilliDadlar', 'Recipes'];

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success(t('link-copied') || 'Link copied to clipboard!');
        } catch (err) {
            console.log(err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = shareUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            toast.success(t('link-copied') || 'Link copied to clipboard!');
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild className='focus:outline-none'>
                <Button
                    variant='ghost'
                    size='icon'
                    className='focus-visible:ring-0 focus-visible:ring-offset-0 [&_svg]:size-6'
                >
                    <Share2Icon />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='p-2'>
                <DropdownMenuItem asChild className='cursor-pointer'>
                    <WhatsappShareButton url={shareUrl} title={title}>
                        <div className='flex items-center gap-2'>
                            <WhatsappIcon size={24} round />
                            <span>WhatsApp</span>
                        </div>
                    </WhatsappShareButton>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className='cursor-pointer p-2'>
                    <TelegramShareButton url={shareUrl} title={title}>
                        <div className='flex items-center gap-2'>
                            <TelegramIcon size={24} round />
                            <span>Telegram</span>
                        </div>
                    </TelegramShareButton>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className='cursor-pointer p-2'>
                    <FacebookShareButton url={shareUrl} hashtag={`#${hashtags[0]}`}>
                        <div className='flex items-center gap-2'>
                            <FacebookIcon size={24} round />
                            <span>Facebook</span>
                        </div>
                    </FacebookShareButton>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className='cursor-pointer p-2'>
                    <TwitterShareButton url={shareUrl} title={title} hashtags={hashtags}>
                        <div className='flex items-center gap-2'>
                            <XIcon size={24} round />
                            <span>X</span>
                        </div>
                    </TwitterShareButton>
                </DropdownMenuItem>
                <DropdownMenuItem
                    asChild
                    className=' flex h-6 w-full cursor-pointer justify-start p-0'
                    onClick={copyToClipboard}
                >
                    <Button className='text-purple-deep flex gap-2 bg-transparent hover:bg-transparent'>
                        <Copy size={24} />
                        <span>{t('copy-link') || 'Copy Link'}</span>
                    </Button>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
